
import { Container } from 'inversify';
import { defineCommand } from '../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Component, component, ObjectManager, property } from '../../concepts/objects';
import { Host } from '../../Host';
import { Point } from '../../platform/geom/Point';
import { Modifiers } from '../../platform/input/Modifiers';
import { Activator } from '../Container';
import { Text } from '../packages/text/Text';
import { lockSetAll, lockSetLayout } from './commands/ComponentCommands';
import { bringToFront, place, sendToBack } from './commands/OrderCommands';
import { clearSelection } from './commands/SelectionCommands';
import { EditorSession } from './EditorSession';
import { Physical } from './entities/Physical';
import { Physicals } from './entities/Physicals';
import { Root } from './entities/Root';
import { HistoryManager } from './history/HistoryManager';
import { MyRoleInfo } from './MyRoleInfo';
import { Selection, SelectionSet } from './Selection';
import { SelectionScaleTransformation } from './tooling/transform/SelectionScaleTransformation';

const get = (objects: ObjectManager, type: string) =>
    Array.from(objects.getAll(Component.describe(type).type)).map(x => x.id);

export const fixLegacyScale = defineCommand(
    'fixLegacyScale',
    'fixLegacyScale',
    r => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);

        dispatch(lockSetAll, [false]);

        const everything = Array.from(objects.getAll(Physical)).filter(x => !x.is(Root));
        const bounds = Physicals.bounds(everything);
        session.update(Selection, SelectionSet.create(everything));

        const start = bounds.topRight();
        const middle = bounds.center();
        const base = Point.vector(middle, start);
        const end = middle.add(base.multiply(1.2));

        const st = r.call(Activator).call(SelectionScaleTransformation);
        objects.update(() => {
            st.configure({
                origin: bounds.bottomLeft(),
                axis: new Point(0.7071067811865475, -0.7071067811865475),
                handle: start,
                fixedMode: false,
                forceSnapping: false
            });
            st.begin({
                position: start,
                modifiers: Modifiers.fromValues(false, true, true),
            });
        });

        objects.update(() => {
            st.complete({
                position: end,
                modifiers: Modifiers.fromValues(false, true, true),
            });
        });

        objects.update(() => {
            const text = objects.getAll(Text);
            for (const t of text) {
                t.fontSize = Math.round(t.fontSize * 1.2);
                t.sizeToContent();
            }
        });

        dispatch(clearSelection, [true]);
        dispatch(lockSetLayout, [true]);
    },
    []
);

@component({ name: 'LocalInitialize' })
export class LocalInitialize extends Component {

    @property({ type: 'string[]' })
    public actions!: string[];
}

export const localInitialize = (scope: Container) => {

    if (Host.offline) return;

    const session = scope.get(EditorSession);
    if (!session.get(MyRoleInfo).canParticipate) return;

    const dispatch = scope.get(CommandDispatcher);
    const objects = scope.get(ObjectManager);
    const history = scope.get(HistoryManager);
    const inits = Array.from(objects.getAll(LocalInitialize));
    if (!inits.length) return;

    objects.update(u => {
        const actions = inits.flatMap(x => x.actions);
        inits.forEach(x => u.delete(x));

        for (const a of actions) {
            const [call, ...args] = a.split(':');

            if (call === 'drop') {
                const [type] = args;
                dispatch(sendToBack, [get(objects, type)]);
            }
            else if (call === 'raise') {
                const [type] = args;
                dispatch(bringToFront, [get(objects, type)]);
            }
            else if (call === 'place') {
                const [type] = args;
                dispatch(place, [get(objects, type)]);
            }
            else if (call === 'fixLegacyScale') {
                dispatch(fixLegacyScale);
            }
        }
    });

    history.clear();
};
